import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Wallet, TrendingUp, TrendingDown, Clock, ArrowUpRight, ArrowDownRight, RefreshCw, Plus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { API } from '@/config/api';

const COLORS = ['#D4AF37', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function Portfolio() {
  const { user } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [trades, setTrades] = useState([]);
  const [cryptoData, setCryptoData] = useState({});
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      // Fetch portfolio
      const portfolioRes = await fetch(`${API}/portfolio`, { credentials: 'include' });
      if (portfolioRes.ok) {
        const data = await portfolioRes.json();
        if (data.length > 0) {
          setPortfolio(data[0]);
          // Fetch current prices for holdings
          await fetchCurrentPrices(data[0].holdings);
        }
      }
      
      // Fetch trades
      const tradesRes = await fetch(`${API}/trades`, { credentials: 'include' });
      if (tradesRes.ok) {
        const data = await tradesRes.json();
        setTrades(data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchCurrentPrices = async (holdings) => {
    try {
      const response = await fetch(`${API}/market/crypto`);
      if (response.ok) {
        const data = await response.json();
        const priceMap = {};
        data.forEach(coin => {
          priceMap[coin.symbol.toUpperCase()] = coin.current_price;
        });
        setCryptoData(priceMap);
      }
    } catch (error) {
      console.error('Price fetch error:', error);
    }
  };
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Calculate portfolio value
  const calculatePortfolioValue = () => {
    if (!portfolio) return 0;
    let holdingsValue = 0;
    portfolio.holdings?.forEach(h => {
      const currentPrice = cryptoData[h.symbol] || h.avg_price;
      holdingsValue += h.quantity * currentPrice;
    });
    return holdingsValue + (portfolio.cash_balance || 0);
  };
  
  // Calculate P/L for holdings
  const calculatePL = (holding) => {
    const currentPrice = cryptoData[holding.symbol] || holding.avg_price;
    const currentValue = holding.quantity * currentPrice;
    const costBasis = holding.quantity * holding.avg_price;
    return currentValue - costBasis;
  };
  
  // Prepare pie chart data
  const getPieChartData = () => {
    if (!portfolio?.holdings?.length) return [];
    const data = portfolio.holdings.map(h => ({
      name: h.symbol,
      value: h.quantity * (cryptoData[h.symbol] || h.avg_price)
    }));
    if (portfolio.cash_balance > 0) {
      data.push({ name: 'Cash', value: portfolio.cash_balance });
    }
    return data;
  };
  
  // Prepare performance chart data (mock historical for demo)
  const getPerformanceData = () => {
    const totalValue = calculatePortfolioValue();
    const days = 7;
    const data = [];
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const variance = (Math.random() - 0.5) * 0.05;
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        value: totalValue * (1 + variance * (i / days))
      });
    }
    return data;
  };
  
  return (
    <div className="min-h-screen bg-[#1c1814] pt-24 pb-12 px-4 sm:px-6 lg:px-8" data-testid="portfolio">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-[#FAFAFA] mb-2">
              My <span className="text-[#D4AF37]">Portfolio</span>
            </h1>
            <p className="font-body text-[#FAFAFA]/60">
              Track your paper trading performance
            </p>
          </div>
          <div className="flex gap-4">
            <Button 
              variant="outline"
              onClick={fetchData}
              className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              data-testid="refresh-portfolio-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Link to="/trading">
              <Button className="btn-gold" data-testid="trade-btn">
                <Plus className="h-4 w-4 mr-2" />
                Trade
              </Button>
            </Link>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Portfolio Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="card-finance md:col-span-2" data-testid="portfolio-value-card">
                <CardHeader className="border-b border-[#D4AF37]/20">
                  <CardTitle className="font-display text-lg text-[#FAFAFA] flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-[#D4AF37]" />
                    Total Portfolio Value
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <p className="font-display text-4xl text-[#D4AF37] mb-4">
                    {formatCurrency(calculatePortfolioValue())}
                  </p>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#FAFAFA]/50 mb-1">Cash Balance</p>
                      <p className="font-display text-xl text-[#FAFAFA]">{formatCurrency(portfolio?.cash_balance)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[#FAFAFA]/50 mb-1">Total Holdings</p>
                      <p className="font-display text-xl text-[#FAFAFA]">{portfolio?.holdings?.length || 0} assets</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Allocation Chart */}
              <Card className="card-finance" data-testid="allocation-chart">
                <CardHeader className="border-b border-[#D4AF37]/20">
                  <CardTitle className="font-display text-lg text-[#FAFAFA]">Allocation</CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  {getPieChartData().length > 0 ? (
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={getPieChartData()}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={70}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {getPieChartData().map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            formatter={(value) => formatCurrency(value)}
                            contentStyle={{ background: '#252540', border: '1px solid rgba(212,175,55,0.3)' }}
                            labelStyle={{ color: '#FAFAFA' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <p className="text-center text-[#FAFAFA]/60 py-8">No holdings yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Performance Chart */}
            <Card className="card-finance mb-8" data-testid="performance-chart">
              <CardHeader className="border-b border-[#D4AF37]/20">
                <CardTitle className="font-display text-lg text-[#FAFAFA]">7-Day Performance</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={getPerformanceData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(212,175,55,0.1)" />
                      <XAxis dataKey="date" stroke="#FAFAFA50" fontSize={12} />
                      <YAxis stroke="#FAFAFA50" fontSize={12} tickFormatter={(v) => `$${(v/1000).toFixed(1)}k`} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ background: '#252540', border: '1px solid rgba(212,175,55,0.3)' }}
                        labelStyle={{ color: '#FAFAFA' }}
                      />
                      <Line type="monotone" dataKey="value" stroke="#D4AF37" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Holdings & Trades */}
            <Tabs defaultValue="holdings">
              <TabsList className="bg-[#252540] border border-[#D4AF37]/20 mb-6">
                <TabsTrigger 
                  value="holdings"
                  className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814]"
                >
                  Holdings
                </TabsTrigger>
                <TabsTrigger 
                  value="trades"
                  className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#1c1814]"
                >
                  Trade History
                </TabsTrigger>
              </TabsList>
              
              {/* Holdings Tab */}
              <TabsContent value="holdings">
                <Card className="card-finance" data-testid="holdings-list">
                  <CardContent className="p-0">
                    {portfolio?.holdings?.length > 0 ? (
                      <div className="divide-y divide-[#D4AF37]/10">
                        {portfolio.holdings.map((holding, index) => {
                          const pl = calculatePL(holding);
                          const currentPrice = cryptoData[holding.symbol] || holding.avg_price;
                          return (
                            <div key={index} className="p-6 flex items-center justify-between" data-testid={`holding-${holding.symbol}`}>
                              <div>
                                <p className="font-body text-[#FAFAFA] font-semibold">{holding.symbol}</p>
                                <p className="text-sm text-[#FAFAFA]/50">{holding.quantity.toFixed(6)} units</p>
                              </div>
                              <div className="text-right">
                                <p className="font-body text-[#FAFAFA]">{formatCurrency(holding.quantity * currentPrice)}</p>
                                <p className={`text-sm flex items-center justify-end ${pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {pl >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
                                  {formatCurrency(Math.abs(pl))} ({((pl / (holding.quantity * holding.avg_price)) * 100).toFixed(2)}%)
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-12 text-center">
                        <Wallet className="h-12 w-12 text-[#FAFAFA]/20 mx-auto mb-4" />
                        <p className="text-[#FAFAFA]/60 mb-4">No holdings yet</p>
                        <Link to="/trading">
                          <Button className="btn-gold">Start Trading</Button>
                        </Link>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
              
              {/* Trades Tab */}
              <TabsContent value="trades">
                <Card className="card-finance" data-testid="trades-list">
                  <CardContent className="p-0">
                    <ScrollArea className="h-[400px]">
                      {trades.length > 0 ? (
                        <div className="divide-y divide-[#D4AF37]/10">
                          {trades.map((trade, index) => (
                            <div key={index} className="p-6 flex items-center justify-between" data-testid={`trade-${trade.trade_id}`}>
                              <div className="flex items-center gap-4">
                                <div className={`p-2 rounded ${trade.side === 'buy' ? 'bg-emerald-500/20' : 'bg-red-500/20'}`}>
                                  {trade.side === 'buy' ? (
                                    <TrendingUp className="h-5 w-5 text-emerald-400" />
                                  ) : (
                                    <TrendingDown className="h-5 w-5 text-red-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-body text-[#FAFAFA]">
                                    {trade.side === 'buy' ? 'Bought' : 'Sold'} {trade.symbol}
                                  </p>
                                  <p className="text-sm text-[#FAFAFA]/50 flex items-center gap-1">
                                    <Clock className="h-3 w-3" />
                                    {formatDate(trade.created_at)}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-body text-[#FAFAFA]">{trade.quantity} @ {formatCurrency(trade.price)}</p>
                                <p className={`text-sm ${trade.side === 'buy' ? 'text-red-400' : 'text-emerald-400'}`}>
                                  {trade.side === 'buy' ? '-' : '+'}{formatCurrency(trade.total)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-12 text-center">
                          <Clock className="h-12 w-12 text-[#FAFAFA]/20 mx-auto mb-4" />
                          <p className="text-[#FAFAFA]/60">No trades yet</p>
                        </div>
                      )}
                    </ScrollArea>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
